/**
 * RoomAvatar — a room's visual, with LINEAGE to its parent network.
 *
 * A room belongs to a network, and the avatar should say so at a glance while
 * still being its own thing:
 *
 *   - INHERIT (belonging): the network's base HUE family. Every room under a
 *     network sits in the same color neighborhood → siblings read as one household.
 *   - VARY (identity):
 *       * SHAPE  — room = CIRCLE (network = rounded square). Shape alone signals
 *         "room vs network" before color even registers.
 *       * HUE    — shifted ±15° from the network hue, seeded by poolId, so quinn
 *         and gizmo are the same family but not identical.
 *       * ICON   — a per-room glyph from the SAME curated set networks use
 *         (seeded by poolId), so the marks feel like one visual system.
 *
 * All deterministic from (networkId, poolId) — same room always renders the same
 * avatar; no storage, retroactive. Mirrors NetworkAvatar's fixed S/L so a room
 * circle and its network square sit together as a coherent pair.
 */
import React from 'react';
import { hashString, pickIcon, networkHue } from './NetworkAvatar.jsx';

// How far a room's hue may drift from the network's — a tight ±15° band keeps
// rooms unmistakably in the family while still separating siblings.
const ROOM_HUE_BAND = 15;

/** Deterministic {Icon, hue, gradient} for a room, in its network's hue family. */
export function roomVisual(networkId, poolId) {
  const baseHue = networkHue(networkId);
  const ph = hashString(`${networkId}:${poolId}`);
  // Signed offset in [-BAND, +BAND] → same family, distinct sibling.
  const offset = (ph % (ROOM_HUE_BAND * 2 + 1)) - ROOM_HUE_BAND;
  const hue = (baseHue + offset + 360) % 360;
  const hue2 = (hue + 40) % 360;
  return {
    Icon: pickIcon(ph),
    hue,
    hue2,
    gradient: `linear-gradient(135deg, hsl(${hue} 62% 52%), hsl(${hue2} 62% 46%))`,
  };
}

export default function RoomAvatar({
  networkId,
  poolId,
  name,
  size = 22,
  className = '',
}) {
  const label = `${name || poolId || 'room'} room icon`;
  const { Icon, gradient } = roomVisual(networkId, poolId);
  const iconSize = Math.round(size * 0.55);
  return (
    <span
      role="img"
      aria-label={label}
      title={name || poolId}
      style={{ width: size, height: size, background: gradient }}
      // rounded-full = CIRCLE → the shape that distinguishes a room from a network.
      className={`inline-flex shrink-0 items-center justify-center rounded-full ${className}`}
      data-testid={`room-avatar-${networkId}-${poolId}`}
    >
      <Icon size={iconSize} strokeWidth={2} color="rgba(255,255,255,0.92)" aria-hidden />
    </span>
  );
}
